import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { baseURL } from "../../../../../Services/api/baseURL";
import { Field, NumberInput, Select, SmallButton } from "./formControls";

export const PricesTab = ({ form, updateField }) => {
  const [storeData, setStoreData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [storeId, setStoreId] = useState("");
  const [sellingPrice, setSellingPrice] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState("percentage");
  const [b2bPrice, setB2bPrice] = useState(0);
  const [b2bDiscount, setB2bDiscount] = useState(0);
  const [b2bDiscountType, setB2bDiscountType] = useState("percentage");
  const [editingIndex, setEditingIndex] = useState(null);

  const priceData = form?.storePricing || [];
  // const finalSellingPrice = discountType === "percentage" ? sellingPrice - (sellingPrice * discount) / 100 : sellingPrice - discount;
  // const b2bFinalPrice = b2bDiscountType === "percentage" ? b2bPrice - (b2bPrice * b2bDiscount) / 100 : b2bPrice - b2bDiscount;
  const finalSellingPrice = Number(
    (
      discountType === "percentage"
        ? sellingPrice - (sellingPrice * discount) / 100
        : sellingPrice - discount
    ).toFixed(2)
  );

  const b2bFinalPrice = Number(
    (
      b2bDiscountType === "percentage"
        ? b2bPrice - (b2bPrice * b2bDiscount) / 100
        : b2bPrice - b2bDiscount
    ).toFixed(2)
  );
  const b2bIncGST = (b2bFinalPrice > 0 ? b2bFinalPrice : 0) * 1.1;

  const selectedStore = storeData.find((s) => s._id === storeId);
  const isBusinessStore =
    selectedStore &&
    /^(business|bus|b)/i.test(selectedStore.storeName.trim());

  useEffect(() => { fetchStores(); }, []);

  const fetchStores = async () => {
    const token = await JSON.parse(localStorage.getItem("token"));
    try {
      const stores = await axios.get(`${baseURL}/api/store/get-all-stores`, {
        headers: { Authorization: `${token}` },
      });
      setStoreData((stores?.data?.data || []).filter((item) => item.status === "active"));
    } catch (error) {
      console.error(error);
    }
  };

  const resetEntryForm = () => {
    setStoreId(""); setSellingPrice(0); setDiscount(0); setDiscountType("percentage");
    setB2bPrice(0); setB2bDiscount(0); setB2bDiscountType("percentage"); setEditingIndex(null);
  };

  const handleAdd = () => {
    if (!storeId) { toast.error("Please select a store"); return; }
    if (editingIndex === null && priceData.some((p) => p.storeId === storeId)) {
      toast.error("Store price already exists"); return;
    }
    const entry = {
      storeId,
      sellingPrice: parseFloat(sellingPrice) || 0,
      discount: parseFloat(discount) || 0,
      discountType,
      finalSellingPrice: parseFloat(finalSellingPrice) || 0,
      b2bPrice: parseFloat(b2bPrice) || 0,
      b2bDiscount: parseFloat(b2bDiscount) || 0,
      b2bDiscountType,
      b2bFinalPrice: parseFloat(b2bFinalPrice) || 0,
    };
    let updated;
    if (editingIndex !== null) {
      updated = [...priceData];
      updated[editingIndex] = entry;
    } else {
      updated = [...priceData, entry];
    }
    updateField("storePricing", updated);
    resetEntryForm();
    setShowForm(false);
  };

  const handleEdit = (index) => {
    const entry = priceData[index];
    setStoreId(entry.storeId);
    setSellingPrice(entry.sellingPrice);
    setDiscount(entry.discount);
    setDiscountType(entry.discountType || "percentage");
    setB2bPrice(entry.b2bPrice || 0);
    setB2bDiscount(entry.b2bDiscount || 0);
    setB2bDiscountType(entry.b2bDiscountType || "percentage");
    setEditingIndex(index);
    setShowForm(true);
  };

  const handleDelete = (index) => {
    updateField("storePricing", priceData.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-800">Store Prices</h4>
        {!showForm && (
          <button
            type="button"
            onClick={() => { resetEntryForm(); setShowForm(true); }}
            className="text-sm font-medium text-pink-600 hover:text-pink-700"
          >
            + Add Store Price
          </button>
        )}
      </div>

      {showForm && (
        <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Retail Price (Inc. GST)</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              <Field label="Store">
                <Select value={storeId} onChange={(e) => setStoreId(e.target.value)}>
                  <option value="">Select store</option>
                  {storeData.map((s) => <option key={s._id} value={s._id}>{s.storeName}</option>)}
                </Select>
              </Field>
              <Field label="Selling Price">
                <NumberInput value={sellingPrice} onChange={(e) => setSellingPrice(Number(e.target.value))} />
              </Field>
              <Field label="Discount">
                <div className="flex gap-2">
                  <Select value={discountType} onChange={(e) => setDiscountType(e.target.value)} className="w-16">
                    <option value="percentage">%</option>
                    <option value="amount">$</option>
                  </Select>
                  <NumberInput value={discount} onChange={(e) => setDiscount(Number(e.target.value))} />
                </div>
              </Field>
              <Field label="Final Price">
                <NumberInput value={finalSellingPrice > 0 ? finalSellingPrice : 0} disabled className="bg-gray-50" />
              </Field>
              <div className="flex items-end">
                <SmallButton type="button" onClick={handleAdd}>{editingIndex !== null ? "Update" : "Add"}</SmallButton>
              </div>
            </div>
          </div>

          {isBusinessStore && (
            <div className="rounded-lg border border-pink-200 bg-pink-50/60 p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-pink-600">B2B / Trade Price (Ex-GST)</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Field label="B2B Price">
                  <NumberInput value={b2bPrice} onChange={(e) => setB2bPrice(Number(e.target.value))} />
                </Field>
                <Field label="B2B Discount">
                  <div className="flex gap-2">
                    <Select value={b2bDiscountType} onChange={(e) => setB2bDiscountType(e.target.value)} className="w-16">
                      <option value="percentage">%</option>
                      <option value="amount">$</option>
                    </Select>
                    <NumberInput value={b2bDiscount} onChange={(e) => setB2bDiscount(Number(e.target.value))} />
                  </div>
                </Field>
                <Field label="B2B Final">
                  <NumberInput value={b2bFinalPrice > 0 ? b2bFinalPrice : 0} disabled className="bg-gray-50" />
                </Field>
                <div className="flex flex-col justify-end rounded-lg bg-pink-100 px-3 py-2 text-xs">
                  <span className="text-gray-500">+10% GST at checkout</span>
                  <span className="font-semibold text-pink-700">${b2bIncGST.toFixed(2)} inc. GST</span>
                </div>
              </div>
              {/* <button
              type="button"
              onClick={() => { resetEntryForm(); setShowForm(false); }}
              className="mt-3 text-xs font-medium text-gray-500 hover:text-gray-700"
            >
              Cancel entry
            </button> */}
            </div>
          )}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Store</th>
              <th className="px-3 py-2 text-left">Selling Price</th>
              <th className="px-3 py-2 text-left">Discount</th>
              <th className="px-3 py-2 text-left">Final Price</th>
              <th className="px-3 py-2 text-left text-pink-600">B2B Price</th>
              <th className="px-3 py-2 text-left text-pink-600">B2B Final</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {priceData.map((data, index) => (
              <tr key={index}>
                <td className="px-3 py-2">{storeData.find((s) => s._id === data.storeId)?.storeName}</td>
                <td className="px-3 py-2">{data.sellingPrice}</td>
                <td className="px-3 py-2">{data.discountType === "percentage" ? `${data.discount} %` : `$${data.discount}`}</td>
                <td className="px-3 py-2">{data.finalSellingPrice}</td>
                <td className="px-3 py-2">{data.b2bPrice > 0 ? `$${data.b2bPrice}` : <span className="text-gray-400">—</span>}</td>
                <td className="px-3 py-2 font-medium text-pink-600">{data.b2bFinalPrice > 0 ? `$${data.b2bFinalPrice}` : <span className="text-gray-400">—</span>}</td>
                <td className="px-3 py-2 text-right">
                  <button type="button" onClick={() => handleEdit(index)} className="mr-3 text-gray-500 hover:text-pink-600"><i className="fa fa-pencil"></i></button>
                  <button type="button" onClick={() => handleDelete(index)} className="text-gray-500 hover:text-red-600"><i className="fa fa-trash-o"></i></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {priceData.length === 0 && <p className="py-8 text-center text-sm text-gray-400">No store prices added yet</p>}
      </div>
    </div>
  );
};